import {
  createContext,
  type FormEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

type ToastTone = "success" | "error" | "info";
type Toast = { id: number; message: string; tone: ToastTone };

const ToastContext = createContext<((message: string, tone?: ToastTone) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((message: string, tone: ToastTone = "info") => {
    const id = Date.now() + Math.floor(Math.random() * 1_000);
    setToasts((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 4_500);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <button
            type="button"
            key={toast.id}
            className={`toast toast-${toast.tone}`}
            onClick={() => setToasts((current) => current.filter((item) => item.id !== toast.id))}
          >
            {toast.message}
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast debe usarse dentro de ToastProvider.");
  return context;
}

export function Modal({
  open,
  title,
  description,
  wide = false,
  children,
  footer,
  onClose
}: {
  open: boolean;
  title: string;
  description?: string;
  wide?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => {
      const focusable = dialogRef.current?.querySelector<HTMLElement>(
        "[autofocus], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]"
      );
      (focusable || dialogRef.current)?.focus();
    }, 0);
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]"
      )].filter((element) => element.offsetParent !== null);
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", handleKey);
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section ref={dialogRef} tabIndex={-1} className={`modal ${wide ? "modal-wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-header">
          <div><h2>{title}</h2>{description && <p>{description}</p>}</div>
          <button type="button" className="icon-button" aria-label="Cerrar" onClick={onClose}>×</button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </section>
    </div>
  );
}

export function ConfirmModal({
  open,
  title,
  description,
  busy = false,
  confirmLabel = "Confirmar",
  onConfirm,
  onClose
}: {
  open: boolean;
  title: string;
  description: string;
  busy?: boolean;
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const safeClose = () => { if (!busy) onClose(); };
  return (
    <Modal
      open={open}
      title={title}
      description={description}
      onClose={safeClose}
      footer={<>
        <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>Cancelar</button>
        <button type="button" className="button button-danger" onClick={onConfirm} disabled={busy}>
          {busy ? "Procesando…" : confirmLabel}
        </button>
      </>}
    >
      <div className="confirmation-copy">Esta acción se aplicará inmediatamente.</div>
    </Modal>
  );
}

export function ErrorState({
  error,
  onRetry
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  const message = error instanceof Error ? error.message : "No se pudo cargar esta sección.";
  return (
    <div className="error-state">
      <strong>Algo salió mal</strong>
      <p>{message}</p>
      {onRetry && <button type="button" className="button button-secondary" onClick={onRetry}>Reintentar</button>}
    </div>
  );
}

export function FormModal({
  open,
  title,
  description,
  submitLabel,
  busy,
  children,
  onSubmit,
  onClose,
  wide,
  submitDisabled
}: {
  open: boolean;
  title: string;
  description?: string;
  submitLabel: string;
  busy?: boolean;
  children: ReactNode;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
  wide?: boolean;
  submitDisabled?: boolean;
}) {
  const safeClose = () => { if (!busy) onClose(); };
  return (
    <Modal
      open={open}
      title={title}
      description={description}
      wide={wide}
      onClose={safeClose}
      footer={null}
    >
      <form onSubmit={onSubmit}>
        <div className="form-grid">{children}</div>
        <div className="modal-footer modal-footer-inline">
          <button type="button" className="button button-secondary" onClick={safeClose} disabled={busy}>Cancelar</button>
          <button type="submit" className="button button-primary" disabled={busy || submitDisabled}>{busy ? "Guardando…" : submitLabel}</button>
        </div>
      </form>
    </Modal>
  );
}
